import { z } from "zod/v4";

import {
  AgenticLogicMetafieldPredicateDescriptions,
  AgenticLogicOptionValuePredicateDescriptions,
  AgenticLogicProductPostFetchWhereDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import { AgenticLogicMetafieldRefSchema } from "./metafield";
import { AgenticLogicTagFilterSchema } from "./server-where";

// ---------------------------------------------------------------------------
// Post-fetch predicates (paged-scan path) — predicates the Storefront API
// CANNOT evaluate, so they run over fetched pages. The key set is pinned by
// AgenticLogicPostFetchFilterKeyList (./filter-keys); every key here costs a scan.
// ---------------------------------------------------------------------------

export const AgenticLogicMetafieldPredicateSchema =
  AgenticLogicMetafieldRefSchema.extend({
    equals: z
      .string()
      .optional()
      .describe(AgenticLogicMetafieldPredicateDescriptions.equals),
    in: z
      .array(z.string())
      .optional()
      .describe(AgenticLogicMetafieldPredicateDescriptions.in),
    exists: z
      .boolean()
      .optional()
      .describe(AgenticLogicMetafieldPredicateDescriptions.exists),
  }).describe(AgenticLogicMetafieldPredicateDescriptions._schema);

export type AgenticLogicMetafieldPredicate = z.infer<
  typeof AgenticLogicMetafieldPredicateSchema
>;

export const AgenticLogicOptionValuePredicateSchema = z
  .object({
    name: z
      .string()
      .describe(AgenticLogicOptionValuePredicateDescriptions.name),
    anyOf: z
      .array(z.string())
      .describe(AgenticLogicOptionValuePredicateDescriptions.anyOf),
  })
  .describe(AgenticLogicOptionValuePredicateDescriptions._schema);

export type AgenticLogicOptionValuePredicate = z.infer<
  typeof AgenticLogicOptionValuePredicateSchema
>;

export const AgenticLogicProductPostFetchWhereSchema = z
  .object({
    title: z
      .object({ contains: z.string() })
      .optional()
      .describe(AgenticLogicProductPostFetchWhereDescriptions.title),
    tags: AgenticLogicTagFilterSchema.optional().describe(
      AgenticLogicProductPostFetchWhereDescriptions.tags
    ),
    metafields: z
      .array(AgenticLogicMetafieldPredicateSchema)
      .optional()
      .describe(AgenticLogicProductPostFetchWhereDescriptions.metafields),
    optionValues: z
      .array(AgenticLogicOptionValuePredicateSchema)
      .optional()
      .describe(AgenticLogicProductPostFetchWhereDescriptions.optionValues),
  })
  .describe(AgenticLogicProductPostFetchWhereDescriptions._schema);

export type AgenticLogicProductPostFetchWhere = z.infer<
  typeof AgenticLogicProductPostFetchWhereSchema
>;

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<
  typeof AgenticLogicMetafieldPredicateDescriptions,
  AgenticLogicMetafieldPredicate
>();
assertDescriptions<
  typeof AgenticLogicOptionValuePredicateDescriptions,
  AgenticLogicOptionValuePredicate
>();
assertDescriptions<
  typeof AgenticLogicProductPostFetchWhereDescriptions,
  AgenticLogicProductPostFetchWhere
>();
